import DataTable from "react-data-table-component";
import { useEffect, useState, useMemo } from "react";
import { getAllPaymentLogs } from "../../api/paymentLogsAPI";
import { Search, Filter, FileDown, CreditCard, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import * as XLSX from 'xlsx';
import PaymentLogDetailModal from '../Modals/Payment/PaymentLogDetailModal ';

const PaymentLogs = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterTransactionType, setFilterTransactionType] = useState("");
  const [filterPaymentMethod, setFilterPaymentMethod] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [activeStatFilter, setActiveStatFilter] = useState(null);
  
  // Date filters - default to current month
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(
    startOfMonth.toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(
    today.toISOString().split('T')[0]
  );
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const params = {
          startDate: startDate,
          endDate: endDate,
        };
        
        const logs = await getAllPaymentLogs(params);
        setData(logs);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [refreshKey, startDate, endDate]);

  // Map transaction types for display
  const getTransactionTypeDisplay = (type) => {
    if (type === "New - Renew") return "SUBSCRIBE/RENEW";
    if (type === "renewal") return "RECURRING";
    return type;
  };

  // Get status color
  const getStatusColor = (decision) => {
    switch(decision) {
      case 'ACCEPT': return 'bg-green-100 text-green-800';
      case 'DECLINE': return 'bg-red-100 text-red-800';
      case 'REVIEW': return 'bg-yellow-100 text-yellow-800';
      case 'ERROR': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Enhanced columns
  const columns = [
    {
      name: "Transaction ID",
      selector: (row) => row.transaction_id,
      sortable: true,
      width: "180px",
      cell: (row) => (
        <div className="text-xs">
          <div className="font-mono font-semibold">{row.transaction_id.substring(0, 16)}...</div>
          <div className="text-gray-500">{new Date(row.createdAt).toLocaleString()}</div>
        </div>
      )
    },
    {
      name: "Client",
      selector: (row) => row.client?.username,
      sortable: true,
      width: "180px",
      cell: (row) => (
        <div className="text-sm">
          <div className="font-semibold">{row.client?.username || 'N/A'}</div>
          <div className="text-xs text-gray-500">{row.client?.mobile || 'N/A'}</div>
        </div>
      )
    },
    {
      name: "Type",
      selector: (row) => row.transaction_type,
      sortable: true,
      width: "140px",
      cell: (row) => (
        <span className={`px-2 py-1 rounded text-xs font-semibold ${
          row.transaction_type === "New - Renew" 
            ? 'bg-blue-100 text-blue-800' 
            : 'bg-purple-100 text-purple-800'
        }`}>
          {getTransactionTypeDisplay(row.transaction_type)}
        </span>
      )
    },
    {
      name: "Amount",
      selector: (row) => row.amount,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <div className="text-sm font-semibold">
          {row.amount} {row.currency}
        </div>
      )
    },
    {
      name: "Payment Method",
      selector: (row) => row.payment_method,
      sortable: true,
      width: "140px",
      cell: (row) => (
        <div className="text-xs">
          <div className="font-semibold">{row.payment_method || 'card'}</div>
          <div className="text-gray-500">{row.card_type || 'N/A'}</div>
        </div>
      )
    },
    {
      name: "Card",
      selector: (row) => row.masked_card_number,
      sortable: false,
      width: "140px",
      cell: (row) => (
        <div className="flex items-center gap-2">
          <CreditCard size={16} className="text-gray-500" />
          <span className="font-mono text-xs">{row.masked_card_number || 'N/A'}</span>
        </div>
      )
    },
    {
      name: "Status",
      selector: (row) => row.decision,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <div className="flex flex-col gap-1">
          <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(row.decision)}`}>
            {row.decision}
          </span>
          <span className="text-xs text-gray-500">{row.status}</span>
        </div>
      )
    },
    {
      name: "Subscription",
      selector: (row) => row.clientSubscription?.subscription_name,
      sortable: false,
      width: "160px",
      cell: (row) => (
        <div className="text-xs">
          <div className="font-semibold">{row.clientSubscription?.subscription_name || 'N/A'}</div>
          <div className="text-gray-500">
            {row.clientSubscription ? 
              `${row.clientSubscription.subscription_cost} ${row.currency}` : 
              'N/A'
            }
          </div>
        </div>
      )
    },
    {
      name: "Actions",
      width: "100px",
      cell: (row) => (
        <button
          onClick={() => setSelectedLog(row)}
          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
        >
          View
        </button>
      )
    }
  ];

  // Calculate statistics
  const statistics = useMemo(() => {
    const total = data.length;
    const successful = data.filter(d => d.decision === 'ACCEPT').length;
    const failed = data.filter(d => d.decision === 'DECLINE' || d.decision === 'ERROR').length;
    const pending = data.filter(d => d.decision === 'REVIEW').length;
    const totalAmount = data.filter(d => d.decision === 'ACCEPT').reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);
    const failedAmount = data.filter(d => d.decision === 'DECLINE' || d.decision === 'ERROR').reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);
    const subscribeRenew = data.filter(d => d.transaction_type === "New - Renew").length;
    const recurring = data.filter(d => d.transaction_type === "renewal").length;
    const avgTransaction = successful > 0 ? totalAmount / successful : 0;

    return {
      total,
      successful,
      failed,
      pending,
      totalAmount,
      failedAmount,
      subscribeRenew,
      recurring,
      avgTransaction,
      successRate: total > 0 ? ((successful / total) * 100).toFixed(1) : 0
    };
  }, [data]);

  // Enhanced filtering
  const filteredData = data.filter((item) => {
    const matchesText = 
      item.transaction_id?.toLowerCase().includes(filterText.toLowerCase()) ||
      item.client?.username?.toLowerCase().includes(filterText.toLowerCase()) ||
      item.client?.mobile?.includes(filterText) ||
      item.client?.email?.toLowerCase().includes(filterText.toLowerCase());
    
    const matchesStatus = filterStatus === "" || item.decision === filterStatus;
    
    const matchesTransactionType = filterTransactionType === "" || item.transaction_type === filterTransactionType;
    
    const matchesPaymentMethod = filterPaymentMethod === "" || item.payment_method === filterPaymentMethod;

    // Stat filter logic
    let matchesStatFilter = true;
    if (activeStatFilter) {
      switch(activeStatFilter) {
        case 'successful':
          matchesStatFilter = item.decision === 'ACCEPT';
          break;
        case 'failed':
          matchesStatFilter = item.decision === 'DECLINE' || item.decision === 'ERROR';
          break;
        case 'pending':
          matchesStatFilter = item.decision === 'REVIEW';
          break;
        case 'subscribeRenew':
          matchesStatFilter = item.transaction_type === "New - Renew";
          break;
        case 'recurring':
          matchesStatFilter = item.transaction_type === "renewal";
          break;
        default:
          matchesStatFilter = true;
      }
    }

    return matchesText && matchesStatus && matchesTransactionType && matchesPaymentMethod && matchesStatFilter;
  });

  // Export to Excel
  const exportToExcel = () => {
    const exportData = filteredData.map(item => ({
      "Transaction ID": item.transaction_id,
      "Network Transaction ID": item.network_transaction_id,
      "Date & Time": new Date(item.createdAt).toLocaleString(),
      "Client Name": item.client?.username || 'N/A',
      "Client Mobile": item.client?.mobile || 'N/A',
      "Client Email": item.client?.email || 'N/A',
      "Transaction Type": getTransactionTypeDisplay(item.transaction_type),
      "Amount": item.amount,
      "Currency": item.currency,
      "Payment Method": item.payment_method,
      "Card Type": item.card_type,
      "Masked Card": item.masked_card_number,
      "Decision": item.decision,
      "Status": item.status,
      "Message": item.message,
      "Reason Code": item.reason_code || 'N/A',
      "Subscription": item.clientSubscription?.subscription_name || 'N/A',
      "Subscription Cost": item.clientSubscription?.subscription_cost || 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payment Logs");
    
    ws['!cols'] = [
      { wch: 25 }, { wch: 25 }, { wch: 20 }, { wch: 20 },
      { wch: 15 }, { wch: 25 }, { wch: 18 }, { wch: 10 },
      { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 18 },
      { wch: 12 }, { wch: 15 }, { wch: 30 }, { wch: 15 },
      { wch: 20 }, { wch: 15 }
    ];
    
    const fileName = `Payment_Logs_${startDate}_to_${endDate}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const handleStatClick = (filterType) => {
    if (activeStatFilter === filterType) {
      setActiveStatFilter(null);
    } else {
      setActiveStatFilter(filterType);
    }
  };

  const handleDateChange = () => {
    setRefreshKey(prev => prev + 1);
  };

  const conditionalRowStyles = [
    {
      when: (row) => row.decision === 'ACCEPT',
      style: {
        borderLeft: "4px solid #10b981",
      },
    },
    {
      when: (row) => row.decision === 'DECLINE' || row.decision === 'ERROR',
      style: {
        borderLeft: "4px solid #ef4444",
      },
    },
    {
      when: (row) => row.decision === 'REVIEW',
      style: {
        borderLeft: "4px solid #f59e0b",
      },
    },
  ];

  return (
    <div className="text-black w-full">
      {loading ? (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="font-semibold">Error loading data:</p>
          <p>{error}</p>
        </div>
      ) : (
        <div className="mt-2">
          {/* Date Range Filters */}
          <div className="flex gap-3 mb-4 bg-white p-4 rounded-lg shadow">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border border-gray-300 rounded-lg p-2 text-sm w-full"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border border-gray-300 rounded-lg p-2 text-sm w-full"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleDateChange}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            <div 
              onClick={() => handleStatClick(null)}
              className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-lg ${
                activeStatFilter === null 
                  ? 'bg-blue-100 border-blue-800 shadow-md' 
                  : 'bg-blue-50 border-blue-600'
              }`}
            >
              <p className="text-xs text-gray-600">Total Transactions</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.total}</p>
              {activeStatFilter === null && <p className="text-xs text-blue-600 mt-1">● Active Filter</p>}
            </div>

            <div 
              onClick={() => handleStatClick('successful')}
              className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-lg ${
                activeStatFilter === 'successful' 
                  ? 'bg-green-100 border-green-800 shadow-md' 
                  : 'bg-green-50 border-green-600'
              }`}
            >
              <p className="text-xs text-gray-600">Successful</p>
              <p className="text-2xl font-bold text-green-600">{statistics.successful}</p>
              <p className="text-xs text-green-600">{statistics.successRate}% success rate</p>
              {activeStatFilter === 'successful' && <p className="text-xs text-green-600 mt-1">● Active Filter</p>}
            </div>

            <div 
              onClick={() => handleStatClick('failed')}
              className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-lg ${
                activeStatFilter === 'failed' 
                  ? 'bg-red-100 border-red-800 shadow-md' 
                  : 'bg-red-50 border-red-600'
              }`}
            >
              <p className="text-xs text-gray-600">Failed</p>
              <p className="text-2xl font-bold text-red-600">{statistics.failed}</p>
              <p className="text-xs text-red-600">QAR {statistics.failedAmount.toFixed(2)} lost</p>
              {activeStatFilter === 'failed' && <p className="text-xs text-red-600 mt-1">● Active Filter</p>}
            </div>

            <div 
              onClick={() => handleStatClick('pending')}
              className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-lg ${
                activeStatFilter === 'pending' 
                  ? 'bg-yellow-100 border-yellow-800 shadow-md' 
                  : 'bg-yellow-50 border-yellow-600'
              }`}
            >
              <p className="text-xs text-gray-600">Under Review</p>
              <p className="text-2xl font-bold text-yellow-600">{statistics.pending}</p>
              {activeStatFilter === 'pending' && <p className="text-xs text-yellow-600 mt-1">● Active Filter</p>}
            </div>

            <div className="p-4 bg-purple-50 rounded-lg border-l-4 border-purple-600">
              <p className="text-xs text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-purple-600">QAR {statistics.totalAmount.toFixed(2)}</p>
              <p className="text-xs text-purple-600">Avg: QAR {statistics.avgTransaction.toFixed(2)}</p>
            </div>

            <div 
              onClick={() => handleStatClick('subscribeRenew')}
              className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-lg ${
                activeStatFilter === 'subscribeRenew' 
                  ? 'bg-indigo-100 border-indigo-800 shadow-md' 
                  : 'bg-indigo-50 border-indigo-600'
              }`}
            >
              <p className="text-xs text-gray-600">Subscribe/Renew</p>
              <p className="text-2xl font-bold text-indigo-600">{statistics.subscribeRenew}</p>
              {activeStatFilter === 'subscribeRenew' && <p className="text-xs text-indigo-600 mt-1">● Active Filter</p>}
            </div>

            <div 
              onClick={() => handleStatClick('recurring')}
              className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-lg ${
                activeStatFilter === 'recurring' 
                  ? 'bg-teal-100 border-teal-800 shadow-md' 
                  : 'bg-teal-50 border-teal-600'
              }`}
            >
              <p className="text-xs text-gray-600">Recurring</p>
              <p className="text-2xl font-bold text-teal-600">{statistics.recurring}</p>
              {activeStatFilter === 'recurring' && <p className="text-xs text-teal-600 mt-1">● Active Filter</p>}
            </div>
          </div>

          {/* Active Filter Indicator */}
          {activeStatFilter && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex justify-between items-center">
              <p className="text-sm text-blue-800">
                <span className="font-semibold">Active Filter:</span> {
                  activeStatFilter === 'successful' ? 'Successful Transactions' :
                  activeStatFilter === 'failed' ? 'Failed Transactions' :
                  activeStatFilter === 'pending' ? 'Under Review' :
                  activeStatFilter === 'subscribeRenew' ? 'Subscribe/Renew Transactions' :
                  activeStatFilter === 'recurring' ? 'Recurring Transactions' : ''
                }
              </p>
              <button 
                onClick={() => setActiveStatFilter(null)}
                className="text-blue-600 hover:text-blue-800 text-sm font-semibold"
              >
                Clear Filter ✕
              </button>
            </div>
          )}

          {/* Search and Filters */}
          <div className="bg-white p-4 rounded-lg shadow mb-4">
            <div className="flex flex-col md:flex-row gap-3 mb-3">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search by transaction ID, client name, mobile, email..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg 
                    focus:ring-blue-500 focus:border-blue-500 block w-full ps-10 p-2.5"
                />
                <div className="absolute inset-y-0 start-0 flex items-center ps-3">
                  <Search className="w-4 h-4 text-gray-500" />
                </div>
              </div>
              
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 
                  rounded-lg transition-colors"
              >
                <Filter size={18} />
                {showFilters ? "Hide" : "Show"} Filters
              </button>

              <button
                onClick={exportToExcel}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white 
                  rounded-lg hover:bg-green-700 transition-colors"
              >
                <FileDown size={18} />
                Export Excel
              </button>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="border border-gray-300 rounded-lg p-2 text-sm w-full"
                  >
                    <option value="">All Statuses</option>
                    <option value="ACCEPT">Accept</option>
                    <option value="DECLINE">Decline</option>
                    <option value="REVIEW">Review</option>
                    <option value="ERROR">Error</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transaction Type
                  </label>
                  <select
                    value={filterTransactionType}
                    onChange={(e) => setFilterTransactionType(e.target.value)}
                    className="border border-gray-300 rounded-lg p-2 text-sm w-full"
                  >
                    <option value="">All Types</option>
                    <option value="New - Renew">Subscribe/Renew</option>
                    <option value="renewal">Recurring</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Method
                  </label>
                  <select
                    value={filterPaymentMethod}
                    onChange={(e) => setFilterPaymentMethod(e.target.value)}
                    className="border border-gray-300 rounded-lg p-2 text-sm w-full"
                  >
                    <option value="">All Methods</option>
                    <option value="card">Card</option>
                    <option value="VISA CREDIT">Visa Credit</option>
                    <option value="MASTERCARD">Mastercard</option>
                  </select>
                </div>
              </div>
            )}

            <div className="mt-3 text-sm text-gray-600">
              Showing {filteredData.length} of {data.length} transactions
            </div>
          </div>

          {/* Data Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <DataTable
              title="PAYMENT LOGS"
              data={filteredData}
              columns={columns}
              pagination
              paginationPerPage={25}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              fixedHeader={true}
              fixedHeaderScrollHeight="600px"
              conditionalRowStyles={conditionalRowStyles}
              highlightOnHover
              pointerOnHover
              dense
            />
          </div>
        </div>
      )}

      {/* Payment Log Detail Modal */}
      {selectedLog && (
        <PaymentLogDetailModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
        />
      )}
    </div>
  );
};

export default PaymentLogs;